import { useState, useEffect } from 'react';
import { Users } from 'lucide-react';
import toast from 'react-hot-toast';
import LoaderService from '../services/LoaderService';
import { GetUnregisterdUsers, GetCustomerSupportUsers } from './apis/get';
import { PostUploadEmployees, PostSendWhatsApp } from './apis/post';
import { PutUpdateEmployee, PutUpdateUnregisteredUser } from './apis/put';
import UnregisteredUsersForm from './UnregisteredUsersForm';

interface User {
  id: number;
  name: string;
  mobile_no: string | null;
  instruction: string;
  status: string;
  feedback: string;
  assigned_to: string | null;
  is_processed: boolean;
  created_at: string;
  updated_at: string;
}

const UnregisteredUsers = () => {
  const [employees, setEmployees] = useState<User[]>([]);
  const [customerSupportUsers, setCustomerSupportUsers] = useState<any[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [editingUser, setEditingUser] = useState<number | null>(null);
  const [editData, setEditData] = useState<Partial<User>>({});
  const [editingInstruction, setEditingInstruction] = useState<number | null>(null);
  const [instructionData, setInstructionData] = useState<string>('');
  const [selectedTemplate] = useState<{[key: number]: string}>({});
  const [messageStatus, setMessageStatus] = useState<{[key: number]: string}>({});
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [currentUser, setCurrentUser] = useState<{mobileNo: string, userId: number} | null>(null);
  const [activeTab, setActiveTab] = useState('form');
  const [loading, setLoading] = useState(false);
  
  // Form fields
  const [formData, setFormData] = useState({
    name: '',
    mobile_no: '',
    instruction: '',
    status: '',
    feedback: '',
    assigned_to: '',
    priority: ''
  });

  useEffect(() => {
    fetchEmployees();
    fetchCustomerSupportUsers();
    // Load existing message status from localStorage
    const existingStatus = JSON.parse(localStorage.getItem('userMessageStatus') || '{}');
    setMessageStatus(existingStatus);
  }, []);

  const fetchCustomerSupportUsers = async () => {
    try {
      const response = await GetCustomerSupportUsers();
      if (response.success) {
        setCustomerSupportUsers(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching customer support users:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      LoaderService.show();
      const response = await GetUnregisterdUsers({ auto_assign: false });
      console.log('GetEmployees response:', response);
      if (response.success) {
        console.log('Employees data:', response.users);
        setEmployees(response.users || []);
        // Clear local message status so API data takes priority
        setMessageStatus({});
      } else {
        console.error('API returned non-success status:', response);
        toast.error('Failed to fetch unregistered users');
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to fetch unregistered users');
    } finally {
      LoaderService.hide();
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    try {
      LoaderService.show();
      const response = await PostUploadEmployees(selectedFile);
      console.log('Upload response:', response);
      if (response.status === 'success') {
        setUploadResult(response);
        toast.success(response.message);
        // Add a small delay before fetching to ensure backend processing is complete
        setTimeout(() => {
          fetchEmployees();
        }, 1000);
        setSelectedFile(null);
      } else {
        console.error('Upload failed with response:', response);
        toast.error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
    } finally {
      LoaderService.hide();
    }
  };

  const handleSendClick = (mobileNo: string, userId: number) => {
    setCurrentUser({mobileNo, userId});
    setShowTemplateModal(true);
  };

  const handleSendWhatsApp = async (templateType: string) => {
    if (!currentUser) return;
    
    try {
      LoaderService.show();
      const response = await PostSendWhatsApp({ mobile_no: currentUser.mobileNo, template_type: templateType });
      if (response.status === 'success') {
        toast.success(`WhatsApp sent to ${response.employee_name}`);
        
        // Save message status locally
        const newMessageStatus = templateType === 'welcome' ? 'welcome msg done' : 'payment msg done';
        const existingData = JSON.parse(localStorage.getItem('userMessageStatus') || '{}');
        existingData[currentUser.userId] = newMessageStatus;
        localStorage.setItem('userMessageStatus', JSON.stringify(existingData));
        
        // Update local state to reflect changes immediately
        setMessageStatus(prev => ({...prev, [currentUser.userId]: newMessageStatus}));
        
        setShowTemplateModal(false);
        setCurrentUser(null);
      } else {
        toast.error('Failed to send WhatsApp');
      }
    } catch (error) {
      toast.error('Failed to send WhatsApp');
    } finally {
      LoaderService.hide();
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user.id);
    setEditData({
      mobile_no: user.mobile_no,
      instruction: user.instruction,
      status: user.status,
      feedback: user.feedback,
      assigned_to: user.assigned_to
    });
  };

  const handleSave = async (userId: number) => {
    try {
      LoaderService.show();
      const response = await PutUpdateEmployee(userId, editData);
      if (response.status === 'success') {
        toast.success('User updated successfully');
        setEditingUser(null);
        setEditData({});
        fetchEmployees();
      } else {
        toast.error('Failed to update user');
      }
    } catch (error) {
      toast.error('Failed to update user');
    } finally {
      LoaderService.hide();
    }
  };

  const handleCancel = () => {
    setEditingUser(null);
    setEditData({});
  };

  const handleDownloadTemplate = async () => {
    try {
      const XLSX = await import('xlsx');
      const templateData = [{
        'Name': 'John Doe',
        'Mobile No.': '1234567890',
        'Instruction': 'Sample instruction',
        'Status': 'Active',
        'Feedback': 'Sample feedback',
        'Assigned To': 'Admin'
      }];
      
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(templateData);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
      
      const buffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'unregistered_users_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleEditInstruction = (userId: number, currentInstruction: string) => {
    setEditingInstruction(userId);
    setInstructionData(currentInstruction);
  };

  const handleSaveInstruction = async (userId: number) => {
    if (!instructionData.trim()) {
      toast.error('Instruction cannot be empty');
      return;
    }

    // Find the user to get assigned_to value
    const user = employees.find(emp => emp.id === userId);
    const payload = {
      user_id: userId,
      instruction: instructionData,
      assigned_to: user?.assigned_to || ''
    };

    try {
      setLoading(true);
      const response = await PutUpdateUnregisteredUser(userId, payload);
      if (response.success) {
        toast.success('Instruction updated successfully');
        setEditingInstruction(null);
        setInstructionData('');
        fetchEmployees();
      } else {
        toast.error('Failed to update instruction');
      }
    } catch (error) {
      toast.error('Failed to update instruction');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInstruction = () => {
    setEditingInstruction(null);
    setInstructionData('');
  };

  const handleFormSubmit = async () => {
    if (!formData.name || !formData.mobile_no) {
      toast.error('Name and Mobile Number are required');
      return;
    }

    setLoading(true);
    try {
      // Create Excel file with single row of form data using correct column headers
      const XLSX = await import('xlsx');
      const excelData = [{
        'Name': formData.name,
        'Mobile No.': formData.mobile_no,
        'Instruction': formData.instruction,
        'Status': formData.status,
        'Feedback': formData.feedback,
        'Assigned To': formData.assigned_to,
        'Priority': formData.priority
      }];
      
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
      const buffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
      const file = new File([buffer], 'single_user.xlsx', { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      const response = await PostUploadEmployees(file);
      if (response.success) {
        toast.success('User added successfully');
        setFormData({
          name: '',
          mobile_no: '',
          instruction: '',
          status: '',
          feedback: '',
          assigned_to: '',
          priority: ''
        });
        fetchEmployees();
      } else {
        toast.error('Failed to add user');
      }
    } catch (error) {
      toast.error('Failed to add user');
    } finally {
      setLoading(false);
    }
  };

  const renderEditableCell = (value: string, field: string, userId: number) => {
    if (editingUser === userId) {
      const isDateField = field === 'Called_Date' || field === 'UpdatedDate';
      const fieldValue = editData[field as keyof User];
      const displayValue = typeof fieldValue === 'boolean' ? fieldValue.toString() : (fieldValue || '');
      return (
        <input
          type="text"
          value={displayValue}
          onChange={(e) => setEditData({...editData, [field]: e.target.value})}
          placeholder={isDateField ? "DD/MM/YYYY" : ""}
          className="bg-white/10 text-white text-xs p-1 rounded border border-white/20 w-full"
        />
      );
    }
    return value || '-';
  };

  const renderStatusCapsule = (status: string) => {
    if (!status || status === '-') {
      return <span className="text-gray-400">-</span>;
    }

    const getStatusStyle = (status: string) => {
      const lowerStatus = status.toLowerCase();
      if (lowerStatus.includes('welcomemsgdone')) {
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      }
      if (lowerStatus.includes('packageinfomsgdone')) {
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      }
      if (lowerStatus.includes('welcome msg done')) {
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      }
      if (lowerStatus.includes('payment msg done')) {
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      }
      return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    };

    const getDisplayText = (status: string) => {
      const lowerStatus = status.toLowerCase();
      if (lowerStatus.includes('welcomemsgdone')) return '✓ Welcome Sent';
      if (lowerStatus.includes('packageinfomsgdone')) return '✓ Package Info Sent';
      if (lowerStatus.includes('welcome msg done')) return '✓ Welcome Sent';
      if (lowerStatus.includes('payment msg done')) return '✓ Payment Info Sent';
      return status;
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border backdrop-blur-sm ${getStatusStyle(status)}`}>
        {getDisplayText(status)}
      </span>
    );
  };

  return (
    <div className="mt-8 space-y-4 text-white p-6">
      <div className="w-full">
        <h1 className="text-2xl font-bold text-yellow-300 mb-6 flex items-center">
          <Users className="mr-2" />
          Unregistered Users
        </h1>
        
        <div className="bg-blue-500/10 rounded-lg border border-blue-500/20 p-4 mb-6">
          <p className="text-sm text-blue-200">
            <strong>Note:</strong> Name and Mobile Number are required fields when adding users manually.
          </p>
        </div>

        <UnregisteredUsersForm
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          formData={formData}
          setFormData={setFormData}
          loading={loading}
          handleFormSubmit={handleFormSubmit}
          handleFileUpload={handleFileUpload}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          uploadResult={uploadResult}
          employees={employees}
          customerSupportUsers={customerSupportUsers}
          renderEditableCell={renderEditableCell}
          renderStatusCapsule={renderStatusCapsule}
          handleEdit={handleEdit}
          handleSave={handleSave}
          handleCancel={handleCancel}
          handleSendClick={handleSendClick}
          editingUser={editingUser}
          fetchEmployees={fetchEmployees}
          handleDownloadTemplate={handleDownloadTemplate}
          handleEditInstruction={handleEditInstruction}
          handleSaveInstruction={handleSaveInstruction}
          handleCancelInstruction={handleCancelInstruction}
          editingInstruction={editingInstruction}
          instructionData={instructionData}
          setInstructionData={setInstructionData}
        />
      </div>
      
      {/* Template Selection Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-white mb-4">Select Template</h3>
            <div className="space-y-3">
              <button
                onClick={() => handleSendWhatsApp('welcome')}
                className="w-full p-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-white text-left transition-colors"
              >
                <div className="font-medium">Welcome Template</div>
                <div className="text-sm text-gray-300">Send welcome message to new users</div>
              </button>
              <button
                onClick={() => handleSendWhatsApp('package_info')}
                className="w-full p-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-white text-left transition-colors"
              >
                <div className="font-medium">Package Info Template</div>
                <div className="text-sm text-gray-300">Send package information and pricing</div>
              </button>
            </div>
            <button
              onClick={() => {setShowTemplateModal(false); setCurrentUser(null);}}
              className="mt-4 w-full px-4 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnregisteredUsers;